/**
 * Consult Scheduling — PURE slot view-model helpers (Door A presentation layer).
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  WHAT THIS IS
 * ─────────────────────────────────────────────────────────────────────────
 *  The deterministic glue between the availability ENGINE (`computeOpenSlots`,
 *  which returns raw `OpenSlot[]`) and the Door A PANEL (`BookConsultPanel`).
 *  It does the three jobs the panel needs done in pure, testable code:
 *
 *    1. filterSlotsByAudience — narrow the engine's slots to the audience the
 *       staffer picked in the "Client: Indian / International" toggle. An 'all'
 *       window always shows for either audience.
 *    2. describeSlot — turn one ISO slot into the display parts the row needs
 *       (weekday / day-number / month / "11:30 AM"), rendered in the viewer's
 *       chosen IANA zone via Intl only (the same zone-arithmetic discipline as
 *       availability.ts — no Date.now inside, caller passes the zone).
 *    3. groupSlotsByDay — bucket the flat slot list into per-day groups, used
 *       for the "N slots across M days" summary line and any day separators.
 *
 *  Like the engine, this layer is React-free and Firebase-free so the Node
 *  native test runner exercises it directly via `test/_tsload.cjs`.
 * ─────────────────────────────────────────────────────────────────────────
 */
import type { Audience } from './types';
import type { OpenSlot } from './availability';

/** The two audiences a staffer can pick in the Door A toggle ('all' is a window tag, never a pick). */
export type PickableAudience = Exclude<Audience, 'all'>;

/**
 * Keep only the slots that match the picked audience. A window tagged 'all'
 * is shown for either audience (it is open to everyone); a window tagged for
 * the OTHER audience is hidden.
 */
export function filterSlotsByAudience(
  slots: OpenSlot[],
  audience: PickableAudience,
): OpenSlot[] {
  return slots.filter(
    (s) => s.audienceTag === audience || s.audienceTag === 'all',
  );
}

/** Display parts for ONE slot row, resolved in `timeZone`. */
export interface SlotDisplay {
  /** Short weekday, e.g. "Wed". */
  weekday: string;
  /** Day of month, e.g. 1. */
  dayNum: number;
  /** Short month, e.g. "Jul". */
  monthShort: string;
  /** 12-hour clock label, e.g. "11:30 AM". */
  time: string;
  /** `YYYY-MM-DD` in `timeZone` — the grouping key. */
  dateISO: string;
}

function partsInZone(
  iso: string,
  timeZone: string,
): Record<string, string> {
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
  const p: Record<string, string> = {};
  for (const part of fmt.formatToParts(new Date(iso))) p[part.type] = part.value;
  return p;
}

/** Resolve the display parts for a slot in the viewer's zone. */
export function describeSlot(slot: OpenSlot, timeZone: string): SlotDisplay {
  const p = partsInZone(slot.startISO, timeZone);
  // `en-CA` numeric gives a stable YYYY-MM-DD grouping key in the zone.
  const dateISO = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(slot.startISO));
  const dayPeriod = (p.dayPeriod || '').toUpperCase();
  return {
    weekday: p.weekday,
    dayNum: parseInt(p.day, 10),
    monthShort: p.month,
    time: `${p.hour}:${p.minute} ${dayPeriod}`.trim(),
    dateISO,
  };
}

/** One day's worth of slots, in calendar order. */
export interface SlotDayGroup {
  /** `YYYY-MM-DD` in `timeZone`. */
  dateISO: string;
  /** Short weekday of the day, e.g. "Wed". */
  weekday: string;
  /** Day of month. */
  dayNum: number;
  /** Short month, e.g. "Jul". */
  monthShort: string;
  /** The slots that fall on this day, ascending. */
  slots: OpenSlot[];
}

/**
 * Bucket a flat (already-sorted) slot list into per-day groups, preserving the
 * ascending order both across days and within each day. Empty in → empty out.
 */
export function groupSlotsByDay(
  slots: OpenSlot[],
  timeZone: string,
): SlotDayGroup[] {
  const groups: SlotDayGroup[] = [];
  const index = new Map<string, SlotDayGroup>();
  for (const slot of slots) {
    const d = describeSlot(slot, timeZone);
    let group = index.get(d.dateISO);
    if (!group) {
      group = {
        dateISO: d.dateISO,
        weekday: d.weekday,
        dayNum: d.dayNum,
        monthShort: d.monthShort,
        slots: [],
      };
      index.set(d.dateISO, group);
      groups.push(group);
    }
    group.slots.push(slot);
  }
  return groups;
}

/** Human label for an audience tag (chip text, sans flag emoji). */
export function audienceLabel(tag: Audience): string {
  switch (tag) {
    case 'indian':
      return 'Indian';
    case 'international':
      return 'International';
    default:
      return 'Everyone';
  }
}

/**
 * DOUBLE-BOOKING GUARD (Vex note, 2026-07-03): booking idempotency is per
 * lead+slot BY DESIGN server-side, so two different leads CAN book the same
 * calendar slot — and `getConsultAvailability` may not know about a
 * just-booked slot yet. A slot that already has a `consultations/{id}` doc is
 * therefore TAKEN and must not be offered again. PURE: drop every slot whose
 * start instant is in the booked set (instant comparison via Date.parse so
 * "…Z" and "+05:30" spellings of the same moment match). Defensive — garbage
 * entries in `bookedStartISOs` are ignored.
 */
export function dropBookedSlots<T extends { startISO: string }>(
  slots: T[],
  bookedStartISOs: readonly string[],
): T[] {
  const taken = new Set(
    (Array.isArray(bookedStartISOs) ? bookedStartISOs : [])
      .map((s) => (typeof s === 'string' ? Date.parse(s) : NaN))
      .filter((n) => Number.isFinite(n)),
  );
  if (taken.size === 0) return slots;
  return slots.filter((s) => !taken.has(Date.parse(s.startISO)));
}
